import type { ReactElement } from 'react';
import { useState } from 'react';
import type { PaddingOrCallback, VictoryAxisProps } from 'victory';
import {
  VictoryArea,
  VictoryAxis,
  VictoryChart,
  VictoryTooltip,
  VictoryVoronoiContainer,
} from 'victory';

import { AreaGradient } from '~/components/LineGraph/AreaGradient';
import { LineGraphFlyout } from '~/components/LineGraph/LineGraphFlyout';
import type { StreamType } from '~/modules/artist-roster/components/TrackDetails/components/TrackStreamsGraph/TrackStreamsGraph';

import { VictoryWmgTheme } from './VictoryWmgTheme';

export type LineGraphData = {
  x: string | number;
  y: string | number;
  delta?: number;
  label?: string;
  date?: string;
}[];

interface LineGraphProps {
  streamType?: StreamType;
  data: LineGraphData[];
  lineColors?: string[];
  containerClassName?: string;
  xAxisProps?: VictoryAxisProps;
  yAxisProps?: VictoryAxisProps;
  tooltip?: ReactElement | undefined;
  tooltipPadding?: PaddingOrCallback;
  tooltipOffset?: number[];
}

const LineGraph = ({
  data,
  streamType,
  containerClassName = '',
  lineColors = ['#000000'],
  xAxisProps = {},
  yAxisProps = {},
  tooltip = undefined,
  tooltipPadding = 12,
  tooltipOffset = [0, 0],
}: LineGraphProps) => {
  const [hovered, setHovered] = useState(false);
  const areaGradientIdPrefix = 'areaGradient_';

  return (
    <div className={containerClassName}>
      {lineColors.map((color, index) => (
        <AreaGradient
          key={index}
          id={areaGradientIdPrefix + index}
          lineColor={color}
        />
      ))}

      <VictoryChart
        padding={{ top: 5, left: 0, right: 40, bottom: 15 }}
        height={100}
        domainPadding={10}
        theme={VictoryWmgTheme}
        containerComponent={
          <VictoryVoronoiContainer
            events={{
              onMouseOver: () => setHovered(true),
              onMouseOut: () => setHovered(false),
            }}
          />
        }
      >
        <VictoryAxis crossAxis {...xAxisProps} />
        <VictoryAxis dependentAxis {...yAxisProps} />
        {data.map((dataPoint, index) => (
          <VictoryArea
            key={index}
            labelComponent={
              <VictoryTooltip
                orientation="right"
                flyoutComponent={
                  <LineGraphFlyout
                    streamType={streamType}
                    dataType={
                      index === 0 && streamType === 'organicStreams'
                        ? 'library'
                        : streamType === 'organicStreams'
                          ? 'search'
                          : 'streams'
                    }
                    data={data}
                    lineColors={lineColors}
                    customFlyoutComponent={tooltip}
                    customFlyoutOffset={tooltipOffset}
                  />
                }
                flyoutPadding={tooltipPadding}
              />
            }
            labels={({ datum }) => datum.label ?? ''}
            interpolation="natural"
            data={dataPoint}
            style={{
              data: {
                stroke: lineColors[index],
                fill: `url(#${areaGradientIdPrefix + index})`,
                fillOpacity: hovered ? 100 : 0,
              },
            }}
          />
        ))}
      </VictoryChart>
    </div>
  );
};

LineGraph.displayName = 'LineGraph';
export { LineGraph };
